import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowUpRight,
  BadgeCheck,
  Banknote,
  Briefcase,
  BriefcaseBusiness,
  CalendarDays,
  CircleCheckBig,
  CircleOff,
  Clock,
  Copy,
  Eye,
  FileText,
  Globe,
  GraduationCap,
  Mail,
  MapPin,
  MessageCircle,
  MessageSquare,
  PenLine,
  Pencil,
  Phone,
  Play,
  RefreshCw,
  Search,
  Send,
  ShieldBan,
  ShieldCheck,
  Sparkles,
  Star,
  Users,
  X,
  XCircle,
  Zap,
} from "lucide-react";
import { FaCheck, FaRegFileAlt } from "react-icons/fa";
import { FiChevronDown } from "react-icons/fi";
import { Button, Modal, Offcanvas } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AddJobModal, { NewJobPayload as NewJobData, ScreeningQuestion } from "components/jobs/AddJobModal";
import StatusReasonModal from "components/common/StatusReasonModal";
import { getCandidateBlockState } from "utils/blocking";
import { notifyCreditChange } from "utils/creditEvents";
import { encryptId } from "utils/urlId";
import ResumeModal from "components/personal_profile/ResumeModal";
import QuickChatOffcanvas from "components/chat/QuickChatOffcanvas";
import UnblockConfirmationModal from "../../ConfirmationModals/unblockConfirmationModal/UnblockConfirmationModal";
import { apiService } from "services/apiService";
import { JobListing } from "components/personal_profile/UserExperience";
import { Candidate } from "types/api";
import { addCandidateLog, seedCandidateStatusFromApi } from "utils/candidateStatusLogs";
import { getCandidateApplicantStatus } from "utils/candidateApplicationStatus";
import { getCandidateDesignation } from "utils/candidateProfile";
import NoProfile from "assets/images/NoProfile.png";
import "assets/css/UserExperience.css";
import JobDetailViewSkeleton from "../../ui/JobDetailViewSkeleton";
import QuickViewSkeleton from "../../ui/QuickViewSkeleton";
import CandidateJobMatchDetails from "components/candidates/CandidateJobMatchDetails";
import { RootState } from "store";
import { useSelector } from "react-redux";
import VerificationRequiredModal from "../../ConfirmationModals/VerificationRequiredModal/VerificationRequiredModal";
import { useCreditConfirm } from 'customhooks/useCreditConfirm';
import CreditConfirmPopup from 'components/payments/CreditConfirmPopup';
import './JobDetailView.css';

interface JobDetailViewProps {
  job: JobListing;
  onBack: () => void;
  onUpdate: (job: JobListing) => void;
  onRefresh?: (selectedJobId?: number | null) => void | Promise<void>;
  onRequestEdit?: (job: JobListing) => void | Promise<void>;
  loading?: boolean;
}

interface JobDetails {
  title: string;
  posted: string;
  applicationDeadline: string;
  description: string;
  skills: { id: number; name: string }[];
  experience: string;
  location: string;
  workMode: string;
  industry: string;
  salary: string;
  openings: string;
  responsibilities: string[];
  requirements: string[];
  benefits: string[];
  department: string;
  qualification: string[];
  genderPreference: string;
  screeningQuestions: ScreeningQuestion[];
  employmentTypes: string[];
  isClosed: boolean;
  isDraft: boolean;
  isPaid?: boolean;
}

type ApplicantFilter =
  | "All"
  | "Applied"
  | "Shortlisted"
  | "Scheduled"
  | "Interviewed"
  | "Hired"
  | "Not Hired";

interface ApplicantRow {
  id: number;
  initials: string;
  name: string;
  email: string;
  exp: string;
  date: string | null;
  status: ApplicantFilter;
  profilePicture: string;
  location: string;
  distance: string;
  designation: string;
  matchPercentage: number;
  isVerified: boolean;
  isPremium: boolean;
  resumeUrl: string;
  resumeLabel: string;
  connected?: boolean | number | string | null;
  blocked_by?: number | string | null;
  is_blocked?: boolean | number | string | null;
}

interface ApplicantCardProps {
  applicant: ApplicantRow;
  jobPositionId: number;
  jobTitle: string;
  statusStyle: Record<string, { bg: string; color: string; border: string }>;
  onStatusChange?: (candidateId: number, status: ApplicantFilter) => void;
  onUnblock?: (candidateId: number) => void;
  connected?: boolean | number | string | null;
}

type JobPayloadOverrides = Partial<NewJobData> & {
  is_draft?: boolean;
};

const EMPLOYMENT_TYPE_LABELS: Record<number, string> = {
  1: "Full-Time",
  2: "Part-Time",
  3: "Contract",
  4: "Temporary",
  5: "Internship",
  6: "Freelance",
  7: "Seasonal",
};

function toEmploymentTypes(job: any): string[] {
  if (!job) return [];

  const resolveItem = (item: any): string => {
    if (!item) return "";
    if (typeof item === "string") {
      const num = Number(item);
      if (!isNaN(num) && EMPLOYMENT_TYPE_LABELS[num]) {
        return EMPLOYMENT_TYPE_LABELS[num];
      }
      return item;
    }
    if (typeof item === "number") {
      return EMPLOYMENT_TYPE_LABELS[item] || `Type ${item}`;
    }
    if (typeof item === "object") {
      if (item.name) return item.name;
      if (item.label) return item.label;
      if (item.title) return item.title;
      const num = Number(item.id || item.value);
      if (!isNaN(num) && EMPLOYMENT_TYPE_LABELS[num]) {
        return EMPLOYMENT_TYPE_LABELS[num];
      }
    }
    return "";
  };

  // 1. Check job_types array
  const jobTypes = job.job_types || job.job_type;
  if (Array.isArray(jobTypes) && jobTypes.length > 0) {
    const extracted = jobTypes.map(resolveItem).filter(Boolean);
    if (extracted.length > 0) return extracted;
  }

  // 2. Check employment_types array
  const empTypes = job.employment_types || job.employmentTypes;
  if (Array.isArray(empTypes) && empTypes.length > 0) {
    const extracted = empTypes.map(resolveItem).filter(Boolean);
    if (extracted.length > 0) return extracted;
  }

  // 3. Fallback: check employment_type_ids
  const rawIds = job.employment_type_ids ?? job.employmentTypeIds;
  const ids = Array.isArray(rawIds) ? rawIds : (rawIds != null ? [rawIds] : []);

  return ids.map(resolveItem).filter(Boolean);
}

function toBenefits(benefits?: { title: string; description: string; created_at?: string }[]) {
  return (benefits || []).map((item) => item.title).filter(Boolean);
}

function decodeHtml(value?: string | null) {
  if (!value) return "";
  const textarea = document.createElement("textarea");
  let decoded = value;

  for (let i = 0; i < 3; i += 1) {
    textarea.innerHTML = decoded;
    const next = textarea.value;
    if (next === decoded) break;
    decoded = next;
  }

  return decoded;
}

function getJobDescription(job: JobListing) {
  return decodeHtml(
    job.description ||
      (job as any).description_text ||
      (job as any).short_description ||
      "",
  );
}

function buildSalary(job: JobListing) {
  if (job.package) return job.package;
  if (job.salary_min != null && job.salary_max != null) {
    return `${job.salary_currency || "INR"} ${job.salary_min} - ${job.salary_max} / ${job.salary_period || "year"}`;
  }
  return "";
}

function formatExperience(job: JobListing) {
  if (job.experience) return job.experience;

  const years = Number(job.work_experience_years || 0);
  const months = Number(job.work_experience_months || 0);
  const parts: string[] = [];

  if (years > 0) {
    parts.push(`${years} year${years === 1 ? "" : "s"}`);
  }
  if (months > 0) {
    parts.push(`${months} month${months === 1 ? "" : "s"}`);
  }

  return parts.join(" ");
}

function formatApplicantDate(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const datePart = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${datePart}, ${timePart}`;
}

function getCandidateInitials(name?: string | null) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);

  if (!parts.length) return "NA";
  return parts.map((part) => part[0]?.toUpperCase() || "").join("");
}

function formatCandidateExperience(candidate: Candidate) {
  return String(
    candidate.work_experience ||
      candidate.experience ||
      candidate.work ||
      "Experience not added",
  ).trim();
}

function mapCandidateToApplicant(candidate: Candidate, unit = "Km"): ApplicantRow {
  const status = getCandidateApplicantStatus(candidate);

  const distanceNum = Number(candidate.distance || 0);
  const effectiveUnit = (candidate as any).show_distance_in || unit;
  const distanceLabel = distanceNum > 0
    ? effectiveUnit === "Km"
      ? `${(distanceNum / 1000).toFixed(2)} km away`
      : `${(distanceNum / 1609).toFixed(2)} mi away`
    : "Distance N/A";

  return {
    id: Number(candidate.id || 0),
    initials: getCandidateInitials(candidate.name),
    name: candidate.name || "Unknown candidate",
    email: candidate.email || "Email not available",
    exp: formatCandidateExperience(candidate),
    date: formatApplicantDate(
      (candidate as any).apply_date ||
      (candidate as any).applied_at ||
      (candidate as any).applied_on ||
      (candidate as any).created_at ||
      (candidate as any).updated_at
    ),
    status,
    profilePicture: candidate.profile_picture || "",
    location: candidate.address || "",
    distance: distanceLabel,
    designation: getCandidateDesignation(candidate),
    matchPercentage: Number(candidate.match_percentage || 0),
    isVerified: Boolean(candidate.phone_number_verify),
    isPremium: Boolean(candidate.is_premium_user),
    resumeUrl: candidate.resume || "",
    connected:  candidate.connected ,
    blocked_by: candidate.blocked_by ?? null,
    is_blocked: (candidate as any).is_blocked ?? (candidate as any).isBlocked ?? null,
    resumeLabel: candidate.resume_label || "Resume.pdf",
  };
}

const getApplicantStatusDate = (candidate: Candidate, status: string) => {
  const data = candidate as any;
  if (status === "Applied") return data.apply_date || data.applied_at || data.applied_on || data.created_at || data.updated_at;
  if (status === "Shortlisted") return data.shortlisted_at || data.updated_at;
  if (status === "Scheduled") return data.scheduled_at || data.updated_at;
  if (status === "Interviewed") return data.interviewed_at || data.updated_at;
  if (status === "Hired") return data.hired_at || data.updated_at;
  if (status === "Not Hired") return data.not_hired_at || data.rejected_at || data.updated_at;
  return data.updated_at || data.created_at || null;
};

function buildJobPayload(job: JobListing, details: JobDetails, overrides: JobPayloadOverrides = {}): NewJobData & { is_draft?: boolean } {
  return {
    id: String(job.id),
    designation_id: Number(job.designation_id || 0),
    job_title: overrides.job_title || details.title,
    skills: job.skills?.map((skill) => skill.id) || [],
    employment_type_ids: job.employment_type_ids || [],
    work_experience_years: Number(job.work_experience_years || 0),
    work_experience_months: Number(job.work_experience_months || 0),
    experience_id: job.experience_id || "",
    description: overrides.description || details.description || getJobDescription(job),
    salary_min: job.salary_min ?? null,
    salary_max: job.salary_max ?? null,
    salary_currency: job.salary_currency || "INR",
    salary_period: job.salary_period || "year",
    openings_count: job.openings_count ?? null,
    location_text: job.location_text || "",
    location_lat: null,
    location_lng: null,
    industry_tags: Array.isArray(job.industry_tags) && job.industry_tags.length > 0
      ? job.industry_tags.map((item: any) => item?.name || item).filter(Boolean)
      : Array.isArray((job as any).industries) && (job as any).industries.length > 0
        ? (job as any).industries.map((s: string) => String(s).trim()).filter(Boolean)
        : typeof (job as any).industry === 'string' && (job as any).industry
          ? (job as any).industry.split(',').map((s: string) => s.trim()).filter(Boolean)
          : [],
    work_mode: (job.work_mode || (job as any).type || "") as any,
    application_deadline: (job as any).application_deadline || null,
    responsibilities: job.responsibilities || [],
    requirements: job.requirements || [],
    benefits: job.benefits || null,
    is_closed: overrides.is_closed ?? Boolean(job.is_closed || job.isClosed),
    is_draft: overrides.is_draft ?? Boolean((job as any).is_draft),
    qualification:
  typeof job.qualification === "string"
    ? JSON.parse(job.qualification)
    : job.qualification || [],
    gender_preference: Array.isArray((job as any).gender_preferences)
      ? (job as any).gender_preferences[0] || ""
      : (job as any).gender_preference || (job as any).gender_preferences || "",
    language_requirements: (job as any).language_requirements || [],
    screening_questions: job.screening_questions || [],
    department: job.department || "",
    ...overrides,
  } as NewJobData & { is_draft?: boolean };
}

function formatPostedDate(value?: string | null): string {
  if (!value) return "";
  
  // Remove "Posted " prefix if present to avoid NaN when parsing,
  // and to avoid "Posted: Posted X" double prefix in UI.
  const cleanValue = value.replace(/^Posted\s+/i, "");

  const date = new Date(cleanValue);
  if (Number.isNaN(date.getTime())) return cleanValue;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }) + ", " + date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDeadlineDate(value?: string | null): string {
  if (!value) return "Not added";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function mapJob(job: JobListing): JobDetails {
  const isClosed = Boolean(job.is_closed || job.isClosed);
  const isDraft = Boolean((job as any).is_draft);
  const rawDeadline = (job as any).application_deadline || (job as any).deadline;

  return {
    title: job.title || "",
    posted: formatPostedDate(job.posted),
    applicationDeadline: formatDeadlineDate(rawDeadline),
    description: getJobDescription(job),
    skills: job.skills || [],
    experience: formatExperience(job),
    location: job.location_text || job.distance || job.location || "",
    workMode: job.work_mode || job.type || "",
    industry: job.industry || "",
    salary: buildSalary(job),
    openings: String(job.openings_count || job.hiring || ""),
    responsibilities: job.responsibilities || [],
    requirements: job.requirements || [],
    benefits: toBenefits(job.benefits),
    department: job.department || "",
    qualification: job.qualification || [],
    genderPreference: Array.isArray(job.gender_preferences) ? job.gender_preferences.join(", ") : job.gender_preferences || "",
    screeningQuestions: job.screening_questions || [],
    employmentTypes: toEmploymentTypes(job),
    isDraft,
    isClosed: Boolean(job.is_closed || job.isClosed),
    isPaid: Boolean((job as any).is_paid ?? (!(job as any).is_draft && job.id)),
  };
}

/* ─────────────────────────────────────────────────────────────
   ApplicantCard — self-contained card with status dropdown,
   resume modal, and full-profile link.
───────────────────────────────────────────────────────────── */
const STATUS_OPTIONS: ApplicantFilter[] = [
  "Applied",
  "Shortlisted",
  "Scheduled",
  "Interviewed",
  "Hired",
  "Not Hired",
];

const getStatusTone = (s: string) => {
  if (s === "Hired")       return { bg: "#eefcf5", color: "#1c8b55", border: "#b7e8cb", strip: "#22C55E" };
  if (s === "Shortlisted") return { bg: "#eef4ff", color: "#2c63d6", border: "#bfd2ff", strip: "#3B82F6" };
  if (s === "Scheduled")   return { bg: "#f3e8ff", color: "#7c3aed", border: "#dec9ff", strip: "#7C3AED" };
  if (s === "Interviewed") return { bg: "#fff8e8", color: "#d68600", border: "#ffe0a7", strip: "#F59E0B" };
  if (s === "Not Hired")   return { bg: "#fff2f2", color: "#e35d5d", border: "#f2b9b9", strip: "#EF4444" };
  return                          { bg: "#fff8e8", color: "#d68600", border: "#ffe0a7", strip: "#94A3B8" }; // Applied
};

const getQuickViewPalette = (percentage: number) =>
  percentage >= 75
    ? {
        tint: "#22C55E",
        header: "linear-gradient(90deg, rgba(134, 239, 172, 0.65) 0%, rgba(229, 242, 255, 0.92) 58%, rgba(244, 247, 251, 0.98) 100%)",
        glow: "rgba(34, 197, 94, 0.20)",
      }
    : percentage >= 35
      ? {
          tint: "#F59E0B",
          header: "linear-gradient(90deg, rgba(253, 224, 71, 0.52) 0%, rgba(246, 227, 188, 0.52) 18%, rgba(233, 240, 253, 0.92) 72%, rgba(244, 247, 251, 0.98) 100%)",
          glow: "rgba(245, 158, 11, 0.18)",
        }
      : {
          tint: "#EF4444",
          header: "linear-gradient(90deg, rgba(252, 165, 165, 0.58) 0%, rgba(249, 214, 220, 0.6) 32%, rgba(233, 240, 253, 0.92) 78%, rgba(244, 247, 251, 0.98) 100%)",
          glow: "rgba(239, 68, 68, 0.16)",
        };

const extractSkillNames = (skills: any): string[] => {
  if (!Array.isArray(skills)) return [];

  return skills
    .flatMap((skill) => {
      if (typeof skill === "string") return [skill];
      if (skill?.name) return [String(skill.name)];
      if (skill?.key) return [String(skill.key)];
      if (Array.isArray(skill?.values)) {
        return skill.values.map((value: any) => String(value?.name || "")).filter(Boolean);
      }
      return [];
    })
    .filter(Boolean)
    .slice(0, 8);
};

/* ─────────────────────────────────────────────────────────────
   ApplicantCardSkeleton — shimmer placeholder while loading
───────────────────────────────────────────────────────────── */
const ApplicantCardSkeleton: React.FC = () => (
  <div
    className="candidate-list-card__applicant-row"
    style={{ ["--candidate-strip" as any]: "#E2E8F0", pointerEvents: "none" }}
  >
    {/* Avatar + ring */}
    <div className="candidate-list-card__avatar-wrap">
      <div className="candidate-list-card__avatar-shell">
        <div
          className="skeleton-shimmer rounded-circle candidate-list-card__avatar"
          style={{ background: "#E2E8F0" }}
        />
        <div
          className="candidate-list-card__match-ring skeleton-shimmer"
          style={{ background: "#E2E8F0" }}
        >
          <div className="candidate-list-card__match-core" />
        </div>
      </div>
    </div>

    {/* Content */}
    <div className="candidate-list-card__content" style={{ flex: 1 }}>
      {/* Name row */}
      <div className="candidate-list-card__title-row" style={{ marginBottom: 6 }}>
        <div className="skeleton-shimmer" style={{ height: 14, width: "38%", borderRadius: 6 }} />
      </div>

      {/* Role row */}
      <div className="candidate-list-card__role-row" style={{ marginBottom: 6 }}>
        <div className="skeleton-shimmer" style={{ height: 12, width: "26%", borderRadius: 6 }} />
      </div>

      {/* Meta row */}
      <div className="candidate-list-card__meta" style={{ gap: 8, marginBottom: 8 }}>
        <div className="skeleton-shimmer" style={{ height: 11, width: "22%", borderRadius: 6 }} />
        <div className="skeleton-shimmer" style={{ height: 11, width: "14%", borderRadius: 6 }} />
        <div className="skeleton-shimmer" style={{ height: 11, width: "28%", borderRadius: 6 }} />
      </div>

      {/* Chips row */}
      <div className="candidate-list-card__chips" style={{ gap: 8 }}>
        <div className="skeleton-shimmer" style={{ height: 28, width: 100, borderRadius: 999 }} />
        <div className="skeleton-shimmer" style={{ height: 28, width: 88, borderRadius: 999 }} />
      </div>
    </div>

    {/* Actions */}
    <div className="candidate-list-card__actions">
      <div className="skeleton-shimmer" style={{ height: 28, width: 90, borderRadius: 999 }} />
    </div>
  </div>
);

interface ApplicantCardProps {
  applicant: ApplicantRow;
  jobPositionId: number;
  jobTitle: string;
  statusStyle: Record<string, { bg: string; color: string; border: string }>;
  onStatusChange?: (candidateId: number, status: ApplicantFilter) => void;
  connected?: boolean | number | string | null | undefined;
  onUnblock?: (candidateId: number) => void;
  isDomainVerified: boolean;
  onVerificationRequired: () => void;
}

const ApplicantCard: React.FC<ApplicantCardProps> = ({ applicant: a, jobPositionId, jobTitle, statusStyle, onStatusChange, onUnblock, connected, isDomainVerified, onVerificationRequired }) => {
  const navigate = useNavigate();
  const blockState = getCandidateBlockState({ blocked_by: a.blocked_by, is_blocked: a.is_blocked });

  // Per-card state
  const [currentStatus, setCurrentStatus] = useState<ApplicantFilter>(a.status);
  const [showStatusMenu, setShowStatusMenu]   = useState(false);
  const [pendingStatus,  setPendingStatus]    = useState<string | null>(null);
  const [showResume,     setShowResume]       = useState(false);
  const [showQuickView, setShowQuickView] = useState(false);
  const [quickViewLoading, setQuickViewLoading] = useState(false);
  const [quickViewData, setQuickViewData] = useState<any>(null);
  const [showQuickChat, setShowQuickChat] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [isUnblocking, setIsUnblocking] = useState(false);
  const [isBlockedState, setIsBlockedState] = useState(blockState.isBlocked);
  const [menuPos,        setMenuPos]          = useState({ top: 0, left: 0, width: 190 });

  const statusBtnRef  = useRef<HTMLDivElement>(null);
  const dropdownRef   = useRef<HTMLDivElement>(null);

  const tone = getStatusTone(currentStatus);
  const blockedTone = statusStyle["Blocked"] ?? { bg: "#fff2f2", color: "#e35d5d", border: "#f2b9b9" };
  const pct          = Math.min(100, Math.max(0, a.matchPercentage));
  const progressAngle = Math.round((pct / 100) * 360);
  const progressColor = pct >= 70 ? "#27a665" : pct >= 40 ? "#d68600" : "#e35d5d";
  const trackColor    = "#e8edf5";
  const quickViewPalette = getQuickViewPalette(pct);
  const quickViewSkills = extractSkillNames(quickViewData?.skills);
  const effectiveResumeUrl = quickViewData?.resume || a.resumeUrl;
  const effectiveResumeLabel = quickViewData?.resume_label || a.resumeLabel || "Resume.pdf";

  const openQuickView = async (event: React.MouseEvent) => {
    event.stopPropagation();
    setShowQuickView(true);
    setQuickViewLoading(true);
    try {
      const result = await apiService.get(`users/get-candidatedetail?other_user_id=${a.id}`) as any;
      setQuickViewData(result?.data || null);
    } catch (error) {
      console.error("Failed to load quick view:", error);
      toast.error("Failed to load candidate details");
    } finally {
      setQuickViewLoading(false);
    }
  };

  /* close dropdown on outside click */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!statusBtnRef.current?.contains(t) && !dropdownRef.current?.contains(t)) {
        setShowStatusMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  useEffect(() => {
    setCurrentStatus(a.status);
  }, [a.status]);

  useEffect(() => {
    setIsBlockedState(getCandidateBlockState({ blocked_by: a.blocked_by, is_blocked: a.is_blocked }).isBlocked);
  }, [a.blocked_by, a.is_blocked]);

  const openStatusMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isDomainVerified && a.resumeUrl) {
      onVerificationRequired();
      return;
    }
    if (statusBtnRef.current) {
      const rect = statusBtnRef.current.getBoundingClientRect();
      const menuH = STATUS_OPTIONS.length * 44;
      const below = window.innerHeight - rect.bottom;
      const above = rect.top;
      setMenuPos({
        top: below < menuH && above > below ? rect.top - menuH - 8 : rect.bottom + 8,
        left: rect.left,
        width: 200,
      });
    }
    setShowStatusMenu(p => !p);
  };

  const handleStatusSelect = (e: React.MouseEvent, option: ApplicantFilter) => {
    e.stopPropagation();
    setShowStatusMenu(false);
    if (isBlockedState || a.blocked_by) {
      toast.error(`Please unblock this user before ${option.toLowerCase()} them.`);
      return;
    }
    if (option === "Applied") {
      setCurrentStatus("Applied");
      return;
    }
    setPendingStatus(option);
  };

  const handleStatusConfirm = async (confirmedStatus: string, reason: string, interviewDate?: string) => {
    if (isBlockedState || a.blocked_by) {
      toast.error(`Please unblock this user before ${confirmedStatus.toLowerCase()} them.`);
      setPendingStatus(null);
      return;
    }

    if (!jobPositionId) {
      toast.error("Job position not found");
      setPendingStatus(null);
      return;
    }
    const payload = {
      other_user_id: a.id,
      job_position_id: jobPositionId,
      reason,
      ...(interviewDate ? { interview_date: interviewDate } : {}),
    };
    try {
      if (confirmedStatus === "Shortlisted") await apiService.addShortlist(payload);
      else if (confirmedStatus === "Scheduled") await apiService.addScheduledInterview(payload);
      else if (confirmedStatus === "Interviewed") await apiService.addInterviewed(payload);
      else if (confirmedStatus === "Hired") await apiService.addHired(payload);
      else if (confirmedStatus === "Not Hired") await apiService.addNotHired(payload);

      setCurrentStatus(confirmedStatus as ApplicantFilter);
      onStatusChange?.(a.id, confirmedStatus as ApplicantFilter);
      addCandidateLog(a.id, {
        status: confirmedStatus,
        date: new Date().toISOString(),
        reason: reason || undefined,
        interviewDate: interviewDate || undefined,
        jobPositionId,
        jobName: jobTitle,
      });
      toast.success(`Candidate marked as ${confirmedStatus}`);
    } catch {
      toast.error("Failed to update status");
    } finally {
      setPendingStatus(null);
    }
  };

  const handleConfirmUnblock = async () => {
    try {
      setIsUnblocking(true);
      const response = await apiService.unBlockUser({ unblock_user_id: a.id });

      if (response?.status === true || response?.success === true) {
        setIsBlockedState(false);
        onUnblock?.(a.id);
        setShowUnlockModal(false);
        toast.success(response?.message || "User unblocked successfully");
        return;
      }

      toast.error(response?.message || "Failed to unblock user");
    } catch (error: any) {
      console.error("Unblock failed:", error?.response?.data || error?.message || error);
      toast.error("Failed to unblock user");
    } finally {
      setIsUnblocking(false);
    }
  };

  return (
    <>
      <div
        className="candidate-list-card__applicant-row"
        style={{ ["--candidate-strip" as any]: tone.strip }}
      >
        {/* ── Avatar + match ring ── */}
        <div className="candidate-list-card__avatar-wrap">
          <div className="candidate-list-card__avatar-shell">
            <img
              src={a.profilePicture || NoProfile}
              alt={a.name}
              className="rounded-circle candidate-list-card__avatar"
              onError={(e) => { (e.currentTarget as HTMLImageElement).src = NoProfile; }}
            />
            {pct > 0 && (
              <div
                className="candidate-list-card__match-ring"
                style={{
                  background: `conic-gradient(${progressColor} 0deg, ${progressColor} ${progressAngle}deg, ${trackColor} ${progressAngle}deg, ${trackColor} 360deg)`,
                  color: progressColor,
                }}
              >
                <div className="candidate-list-card__match-core">{pct}%</div>
              </div>
            )}
          </div>
        </div>

        {/* ── Content ── */}
        <div className="candidate-list-card__content">
          {/* Name row */}
          <div className="candidate-list-card__title-row">
            <div className="candidate-list-card__name-wrap">
              <h6 className="mb-0 fw-bold candidate-list-card__name">
                {a.name}
                {a.isVerified && <BadgeCheck size={15} className="candidate-list-card__verified" />}
              </h6>
              {a.isPremium && <span className="candidate-list-card__premium">Premium</span>}
            </div>
          </div>

          {/* Role + experience */}
          {(a.designation || a.exp) && (
            <div className="candidate-list-card__role-row">
              {a.designation && (
                <span className="candidate-list-card__role">
                  <BriefcaseBusiness size={13} /> {a.designation}
                </span>
              )}
              {a.exp && a.exp !== "Experience not added" && (
                <span className="candidate-list-card__exp">{a.exp}</span>
              )}
            </div>
          )}

          {/* Meta row */}
          <div className="candidate-list-card__meta">
            {a.location && (
              <span className="candidate-list-card__meta-item">
                <MapPin size={13} /> {a.location}
              </span>
            )}
            {a.location && <span className="candidate-list-card__meta-dot" />}
            <span className="candidate-list-card__meta-item">{a.distance}</span>
            <span className="candidate-list-card__meta-dot" />
            <span className="candidate-list-card__meta-item">
              <CalendarDays size={13} /> Applied: {a.date ?? "Date not available"}
            </span>
          </div>

          {/* Chips row — status dropdown or blocked badge */}
          <div className="candidate-list-card__chips">
            <div className="position-relative" ref={statusBtnRef}>
                        <button
                  type="button"
                  onClick={openStatusMenu}
                  className="candidate-list-card__status-chip"
                  style={{ borderColor: tone.border, background: tone.bg, color: tone.color }}
                >
                  <span className="candidate-list-card__status-chip-label">
                    <span className="candidate-list-card__status-dot" style={{ background: tone.color }} />
                    {currentStatus}
                  </span>
                  <FiChevronDown size={13} />
                </button>
              
            </div>

            <button
              type="button"
              className="candidate-list-card__chip"
              onClick={(e) => { 
                if (!isDomainVerified && a.resumeUrl) {
                  onVerificationRequired();
                  return;
                }
                e.stopPropagation(); 
                setShowResume(true); 
              }}
            >
              <FaRegFileAlt size={12} />
              {a.resumeLabel || "Resume"}
            </button>
          </div>
        </div>

        {/* ── Actions column ── */}
        <div className="candidate-list-card__actions">
          <div className="candidate-list-card__links">
            <button
              type="button"
              className="candidate-list-card__mini-link"
              onClick={openQuickView}
            >
              <Eye size={13} />
              Quick view
            </button>
            {!isBlockedState && (
              <button
                type="button"
                className="candidate-list-card__mini-link candidate-list-card__mini-link--success"
                onClick={(e) => { e.stopPropagation(); setShowQuickChat(true); }}
              >
                <MessageCircle size={13} />
                Quick chat
              </button>
            )}
            {isBlockedState ? (
              <button
                type="button"
                className="candidate-list-card__mini-link candidate-list-card__mini-link--primary"
                onClick={(e) => { e.stopPropagation(); setShowUnlockModal(true); }}
              >
                Unblock
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {/* ── Status dropdown portal ── */}
      {showStatusMenu && createPortal(
        <div
          ref={dropdownRef}
          onMouseDown={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
          style={{
            position: "fixed",
            top: Math.max(12, menuPos.top),
            left: menuPos.left,
            width: menuPos.width,
            background: "#fff",
            border: "1px solid #D9E2EC",
            borderRadius: 16,
            boxShadow: "0 18px 40px rgba(15,23,42,0.14)",
            overflow: "hidden",
            zIndex: 4000,
          }}
        >
          {STATUS_OPTIONS.map((option, idx) => {
            const ot = getStatusTone(option);
            const isActive = currentStatus === option;
            return (
              <button
                key={option}
                type="button"
                onClick={e => 
                  handleStatusSelect(e, option)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 14px",
                  border: "none",
                  borderBottom: idx !== STATUS_OPTIONS.length - 1 ? "1px solid #EEF2F7" : "none",
                  background: isActive ? "#EEF4FF" : "#fff",
                  color: ot.color,
                  fontSize: 13,
                  fontWeight: isActive ? 700 : 500,
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: ot.color, flexShrink: 0 }} />
                  {option}
                </span>
                {isActive && <FaCheck size={12} style={{ color: ot.color, flexShrink: 0 }} />}
              </button>
            );
          })}
        </div>,
        document.body,
      )}

      {/* ── Status reason modal ── */}
      {pendingStatus && (
        <StatusReasonModal
          show
          initialStatus={pendingStatus}
          candidateName={a.name}
          candidateAvatar={a.profilePicture}
          candidateRole={a.designation}
          candidateLocation={a.location}
          preselectedJobId={jobPositionId}
          preselectedJobTitle={jobTitle}
          lockJobSelection
          onConfirm={handleStatusConfirm}
          onCancel={() => setPendingStatus(null)}
        />
      )}

      {/* ── Resume modal ── */}
      <ResumeModal
        show={showResume}
        onHide={() => setShowResume(false)}
        candidateName={quickViewData?.name || a.name}
        designation={getCandidateDesignation(quickViewData, a.designation)}
        location={quickViewData?.address || a.location}
        resumeUrl={effectiveResumeUrl}
        resumeLabel={effectiveResumeLabel}
        profilePic={quickViewData?.profile_pic || a.profilePicture}
      />

      <UnblockConfirmationModal
        show={showUnlockModal}
        onHide={() => setShowUnlockModal(false)}
        onConfirm={handleConfirmUnblock}
        userName={a?.name}
        isUnblocking={isUnblocking}
      />

      <Offcanvas
        show={showQuickView}
        onHide={() => setShowQuickView(false)}
        placement="end"
        className="candidate-quickview"
      >
        <Offcanvas.Body className="candidate-quickview__body">
          {quickViewLoading ? (
            <QuickViewSkeleton />
          ) : (
            <>
              <div className="candidate-quickview__header" style={{ background: quickViewPalette.header, ["--quickview-accent" as any]: quickViewPalette.tint, ["--quickview-glow" as any]: quickViewPalette.glow }}>
                <button type="button" className="candidate-quickview__close" onClick={() => setShowQuickView(false)}>
                  <X size={18} />
                </button>

                <div className="candidate-quickview__hero">
                  <div className="candidate-quickview__avatar-shell">
                    <img
                      src={quickViewData?.profile_pic || a.profilePicture || NoProfile}
                      alt={quickViewData?.name || a.name}
                      className="candidate-quickview__avatar"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).src = NoProfile;
                      }}
                    />
                    <div
                      className="candidate-quickview__ring"
                      style={{
                        background: `conic-gradient(${quickViewPalette.tint} 0deg, ${quickViewPalette.tint} ${progressAngle}deg, #E8EDF5 ${progressAngle}deg, #E8EDF5 360deg)`,
                        color: quickViewPalette.tint,
                      }}
                    >
                      <div className="candidate-quickview__ring-core">{pct}%</div>
                    </div>
                  </div>

                  <div className="candidate-quickview__hero-copy">
                    <div className="candidate-quickview__hero-name">
                      <h3>{quickViewData?.name || a.name}</h3>
                      {a.isVerified ? <BadgeCheck size={18} /> : null}
                    </div>
                    <p>{getCandidateDesignation(quickViewData, a.designation)}</p>
                    <div className="candidate-quickview__hero-tags">
                      <span className="candidate-quickview__status-tag" style={{ color: tone.color, background: tone.bg }}>
                        <span className="candidate-list-card__status-dot" style={{ background: tone.color }} />
                        {currentStatus}
                      </span>
                      {a.exp && a.exp !== "Experience not added" ? <span className="candidate-quickview__muted-tag">{a.exp}</span> : null}
                    </div>
                  </div>
                </div>
              </div>

              <div className="candidate-quickview__content">
                {/* ── Blocked user restriction overlay ── */}
                {isBlockedState ? (
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "48px 24px",
                    textAlign: "center",
                    gap: 16,
                  }}>
                    <div style={{
                      width: 64, height: 64, borderRadius: "50%",
                      background: "#FEF2F2",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <ShieldBan size={28} color="#DC2626" />
                    </div>
                    <div>
                      <h6 style={{ fontWeight: 700, color: "#1F2937", marginBottom: 6 }}>Profile Restricted</h6>
                      <p style={{ fontSize: 13, color: "#6B7280", margin: 0, lineHeight: 1.6 }}>
                        You have blocked this user. Unblock them to view their full profile details.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="candidate-quickview__cta candidate-quickview__cta--primary"
                      style={{ marginTop: 8 }}
                      onClick={() => {
                        
                        setShowQuickView(false);
                        setShowUnlockModal(true);
                      }}
                    >
                      <ShieldCheck size={16} />
                      Unblock User
                    </button>
                  </div>
                ) : (
                  <>
                <div className="candidate-quickview__stats">
                  <div className="candidate-quickview__stat-card">
                    <strong style={{ color: quickViewPalette.tint }}>{pct}%</strong>
                    <span>Match</span>
                  </div>
                  <div className="candidate-quickview__stat-card">
                    <strong>{a.distance.replace(" away", "")}</strong>
                    <span>Distance</span>
                  </div>
                  <div className="candidate-quickview__stat-card">
                    <strong>{quickViewData?.matches ?? "0"}</strong>
                    <span>Mutual</span>
                  </div>
                </div>

                <div className="candidate-quickview__section">
                  <h6>About</h6>
                  <p>{quickViewData?.about || "No about information available."}</p>
                </div>

                {quickViewSkills.length ? (
                  <div className="candidate-quickview__section">
                    <h6>Skills</h6>
                    <div className="candidate-quickview__chips">
                      {quickViewSkills.map((skill) => (
                        <span key={skill} className="candidate-quickview__chip">{skill}</span>
                      ))}
                    </div>
                  </div>
                ) : null}

                <CandidateJobMatchDetails
                  jobMatches={quickViewData?.job_matches}
                  primaryJobMatch={quickViewData?.primary_job_match}
                  fallbackJobName={quickViewData?.matched_job_name}
                  fallbackMatchPercentage={Number(quickViewData?.primary_job_match?.match_percentage ?? pct)}
                />

                <div className="candidate-quickview__section">
                  <h6>Details</h6>
                  <div className="candidate-quickview__detail-list">
                    <div className="candidate-quickview__detail-item">
                      <BriefcaseBusiness size={16} />
                      {/* <div>
                        <span>Primary Job</span>
                        <strong>{quickViewData?.primary_job_match?.job_name || quickViewData?.matched_job_name || "Not available"}</strong>
                      </div> */}
                    </div>
                    <div className="candidate-quickview__detail-item">
                      <MapPin size={16} />
                      <div>
                        <span>Location</span>
                        <strong>{quickViewData?.address || a.location || "Not available"}</strong>
                      </div>
                    </div>
                    {quickViewData?.email || a.email ? (
                      <div className="candidate-quickview__detail-item">
                        <Mail size={16} />
                        <div>
                          <span>Email</span>
                          <strong>{quickViewData?.email || a.email}</strong>
                        </div>
                      </div>
                    ) : null}
                    {quickViewData?.phone_number ? (
                      <div className="candidate-quickview__detail-item">
                        <Phone size={16} />
                        <div>
                          <span>Phone</span>
                          <strong>{quickViewData.phone_number}</strong>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="candidate-quickview__section">
                  <h6>Attachments</h6>
                  <div className="candidate-quickview__attachments">
                    <button
                      type="button"
                      className="candidate-quickview__attachment"
                      onClick={() => {
                        if (!isDomainVerified && a.resumeUrl) {
                          onVerificationRequired();
                          return;
                        }
                        if (effectiveResumeUrl) {
                          setShowResume(true);
                        }
                      }}
                    >
                      <span className="candidate-quickview__attachment-icon"><FaRegFileAlt size={16} /></span>
                      <span>
                        <strong>{effectiveResumeLabel}</strong>
                        <small>{effectiveResumeUrl ? "View document" : "Resume not uploaded"}</small>
                      </span>
                    </button>
                    {quickViewData?.pitch_id ? (
                      <button type="button" className="candidate-quickview__attachment">
                        <span className="candidate-quickview__attachment-icon candidate-quickview__attachment-icon--pitch"><MessageSquare size={16} /></span>
                        <span>
                          <strong>Pitch video</strong>
                          <small>Available on full profile</small>
                        </span>
                      </button>
                    ) : null}
                  </div>
                  <div className="candidate-quickview__footer-action">
                    <button
                      type="button"
                      className="candidate-quickview__fullview"
                      onClick={() => {
                        if (isBlockedState) {
                          toast.error(blockState.blockedByMe ? "Please unblock this user before viewing their profile details." : "This profile is unavailable because the user has blocked you.");
                          return;
                        }
                        navigate(`/candidate/${encryptId(a.id)}`);
                      }}
                    >
                      Full View
                      <ArrowUpRight size={14} />
                    </button>
                  </div>
                </div>
                  </>
                )}
              </div>
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      <QuickChatOffcanvas
        show={showQuickChat}
        onHide={() => setShowQuickChat(false)}
        candidateId={String(a.id)}
        candidateName={a.name}
        candidateImage={a.profilePicture}
        jobId={String(jobPositionId)}
        jobTitle={jobTitle}
        connected={connected != null ? (typeof connected === 'string' ? Number(connected) : connected) : undefined}
      />
    </>
  );
};

const resolveJobId = (response: any, fallbackId?: string | number | null): number | null => {
  const candidates = [
    response?.data?.id,
    response?.data?.job_position_id,
    response?.data?.job_id,
    response?.data?.insertId,
    response?.data?.data?.id,
    response?.data?.data?.job_position_id,
    response?.data?.data?.job_id,
    response?.id,
    response?.job_position_id,
    response?.job_id,
    fallbackId,
  ];

  const selected = candidates.find((value) => value !== undefined && value !== null && value !== "");
  return selected == null ? null : Number(selected);
};

const JobDetailView: React.FC<JobDetailViewProps> = ({ job, onBack, onUpdate, onRefresh, onRequestEdit, loading = false }) => {
  const [view, setView] = useState<"details" | "edit">("details");
  const [details, setDetails] = useState<JobDetails>(() => mapJob(job));
  const [activeTab, setActiveTab] = useState<"overview" | "candidates">("overview");
  const [distanceUnit, setDistanceUnit] = useState<string>("Km");
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const state = useSelector((state: RootState) => state.auth);
  const isDomainVerified = state.verified_domain;

  // Fetch distance unit from settings once on mount
  useEffect(() => {
    apiService.getSettings().then((res: any) => {
      const unit = res?.data?.show_distance_in;
      if (unit) setDistanceUnit(unit);
    }).catch(() => {});
  }, []);

  // Modal state
  const [showDuplicate, setShowDuplicate] = useState(false);
  const [showClosePosition, setShowClosePosition] = useState(false);
  const [statusWarningModal, setStatusWarningModal] = useState<{
    show: boolean;
    title: string;
    message: string;
    deadlineInfo?: string;
  }>({ show: false, title: '', message: '' });
  const [duplicateTitle, setDuplicateTitle] = useState("");
  const [duplicateDesignationId, setDuplicateDesignationId] = useState<number>(0);
  const [duplicateDesignationList, setDuplicateDesignationList] = useState<{ id: number; name: string }[]>([]);
  const [isDuplicateDesignationOpen, setIsDuplicateDesignationOpen] = useState(false);
  const [isLoadingDuplicateDesignations, setIsLoadingDuplicateDesignations] = useState(false);
  const duplicateTitleRef = useRef<HTMLDivElement>(null);
  const [applicantSearch, setApplicantSearch] = useState("");
  const [applicantFilter, setApplicantFilter] = useState<ApplicantFilter>("All");
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [applicants, setApplicants] = useState<ApplicantRow[]>([]);
  const [isLoadingApplicants, setIsLoadingApplicants] = useState(false);

  const filteredApplicants = applicants.filter(a => {
    const matchesSearch = !applicantSearch || a.name.toLowerCase().includes(applicantSearch.toLowerCase()) || a.email.toLowerCase().includes(applicantSearch.toLowerCase());
    const matchesFilter = applicantFilter === "All" || a.status === applicantFilter;
    return matchesSearch && matchesFilter;
  });



  const handleApplicantStatusChange = (candidateId: number, status: ApplicantFilter) => {
    setApplicants((prev) =>
      prev.map((applicant) =>
        applicant.id === candidateId ? { ...applicant, status } : applicant,
      ),
    );
  };

  const handleApplicantUnblocked = (candidateId: number) => {
    setApplicants((prev) =>
      prev.map((applicant) =>
        applicant.id === candidateId
          ? { ...applicant, blocked_by: null, is_blocked: false }
          : applicant,
      ),
    );
  };

  const statusStyle: Record<string, { bg: string; color: string; border: string }> = {
    Shortlisted: { bg: "#edfbf3", color: "#27a665", border: "#aae4c3" },
    Scheduled: { bg: "#eef4ff", color: "#2c63d6", border: "#bfd2ff" },
    Interviewed: { bg: "#eef4ff", color: "#2c63d6", border: "#bfd2ff" },
    Applied: { bg: "#fff8e8", color: "#d68600", border: "#ffe0a7" },
    Hired: { bg: "#eefcf5", color: "#1c8b55", border: "#b7e8cb" },
    "Not Hired": { bg: "#fff2f2", color: "#e35d5d", border: "#f2b9b9" },
    Blocked: { bg: "#fff2f2", color: "#e35d5d", border: "#f2b9b9" },
  };

  useEffect(() => {
    setDetails(mapJob(job));
    setView("details");
    setActiveTab("overview");
    setApplicants([]);
    setApplicantSearch("");
    setApplicantFilter("All");
  }, [job]);

  useEffect(() => {
    if (activeTab !== "candidates") return;

    let isMounted = true;

    const fetchApplicants = async () => {
      try {
        setIsLoadingApplicants(true);
        const response = await apiService.getApplicantsList({
          limit: 100,
          page: 1,
          search: null,
          filters: {
            allProfile: true,
            jobPositionId: Number(job.id),
          },
          job_position_id: Number(job.id),
        } as any);

        const rawList = response?.data?.users ?? response?.data?.candidates;
        const nextApplicants = Array.isArray(rawList)
          ? rawList.map((c: any) => mapCandidateToApplicant(c, distanceUnit))
          : [];
        if (Array.isArray(rawList)) {
          seedCandidateStatusFromApi(
            rawList.map((candidate: Candidate) => {
              const status = getCandidateApplicantStatus(candidate);
              return {
                id: Number(candidate.id || 0),
                status,
                date: getApplicantStatusDate(candidate, status),
                jobPositionId: Number(job.id),
                jobName: job.title,
              };
            }),
          );
        }

        if (isMounted) {
          setApplicants(nextApplicants);
        }
      } catch (error) {
        console.error("Failed to fetch applicants", error);
        if (isMounted) {
          setApplicants([]);
          toast.error("Unable to load applicants right now.");
        }
      } finally {
        if (isMounted) {
          setIsLoadingApplicants(false);
        }
      }
    };

    fetchApplicants();

    return () => {
      isMounted = false;
    };
  }, [activeTab, job.id, job.title]);

  const openDuplicate = () => {
    setDuplicateTitle(`${details.title} (Copy)`);
    setDuplicateDesignationId(Number((job as any).designation_id) || 0);
    setDuplicateDesignationList([]);
    setIsDuplicateDesignationOpen(false);
    setShowDuplicate(true);
    // Pre-load designations for the current title so the dropdown is ready
    fetchDuplicateDesignations(details.title);
  };

  // Close designation dropdown on outside click
  useEffect(() => {
    if (!isDuplicateDesignationOpen) return;
    const handler = (e: MouseEvent) => {
      if (duplicateTitleRef.current && !duplicateTitleRef.current.contains(e.target as Node)) {
        setIsDuplicateDesignationOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isDuplicateDesignationOpen]);

  const fetchDuplicateDesignations = async (key: string) => {
    if (!key.trim()) { setDuplicateDesignationList([]); return; }
    try {
      setIsLoadingDuplicateDesignations(true);
      const data = await apiService.getDesignations(key);
      if (data?.status) setDuplicateDesignationList(data.data || []);
      else setDuplicateDesignationList([]);
    } catch { setDuplicateDesignationList([]); }
    finally { setIsLoadingDuplicateDesignations(false); }
  };
  const handleDuplicate = async () => {
    setIsDuplicating(true);
    try {
      const response = await apiService.addDuplicateLookingFor(
        buildJobPayload(job, details, {
          id: undefined as any,
          job_title: `${details.title} (Copy)`,
          designation_id: duplicateDesignationId || (job as any).designation_id,
          is_closed: false,
          is_draft: true,
        }),
      );

      if (!response?.status) {
        toast.error(response?.message || "Failed to duplicate job");
        return;
      }

      await onRefresh?.();
      setShowDuplicate(false);
      onBack();
      toast.success(`"${duplicateTitle.trim() || `${details.title} (Copy)`}" created successfully`);
    } catch {
      toast.error("Failed to duplicate job");
    } finally { setIsDuplicating(false); }
  };
  const handleClosePosition = async () => {
    setIsClosing(true);
    try {
      const res = await apiService.toggleJobStatus(Number(job.id), { action: 'close' });
      if (res?.status) {
        toast.success("Position closed successfully");
        const updated = { ...job, is_closed: true, isClosed: true };
        onUpdate(updated);
        setDetails(mapJob(updated));
      } else { toast.error(res?.message || "Failed to close position"); }
    } catch { toast.error("Failed to close position"); }
    finally { setIsClosing(false); setShowClosePosition(false); }
  };

  const [jobToToggle, setJobToToggle] = useState<JobListing | null>(null);
  const [isToggling, setIsToggling] = useState(false);

  const {
    requestConfirm,
    isOpen: isCreditPopupOpen,
    popupOptions: creditPopupOptions,
    handleConfirm: handleCreditConfirm,
    handleCancel: handleCreditCancel,
    handleNeverShowAgain: handleCreditNeverShowAgain,
  } = useCreditConfirm();

  const [inlineDeadline, setInlineDeadline] = useState<string>('');
  const [isUpdatingDeadline, setIsUpdatingDeadline] = useState(false);

  const handleUpdateDeadlineAndPublish = async () => {
    if (!inlineDeadline) {
      toast.error("Please select a new deadline first");
      return;
    }

    setIsUpdatingDeadline(true);
    try {
      const payload = {
        id: job.id,
        is_closed: false,
        is_draft: false,
        application_deadline: inlineDeadline,
        designation_id: (job as any).designation_id,
        job_title: job.title,
        employment_type_ids: job.employment_type_ids || [],
        industry_tags: Array.isArray(job.industry_tags) ? job.industry_tags.map((t: any) => t.id || t.name || t) : [],
        work_mode: job.work_mode || '',
        experience_id: job.experience_id || (job as any).experiance_id,
        work_experience_years: job.work_experience_years || 0,
        work_experience_months: job.work_experience_months || 0,
        salary_min: job.salary_min ?? null,
        salary_max: job.salary_max ?? null,
        salary_currency: job.salary_currency || 'INR',
        salary_period: job.salary_period || 'year',
        salary_visibility: job.salary_min != null ? 'range' : 'hidden',
        openings_count: job.openings_count || 1,
        location_text: job.location_text || '',
        location_lat: (job as any).location_lat ?? null,
        location_lng: (job as any).location_lng ?? null,
      };
      
      const updateRes = await apiService.addLookingForWithStep(payload, 'basics');
      
      if (updateRes) {
        const res = await apiService.bulkPublishJobs([Number(job.id)]);
        if (res?.status || updateRes?.status) {
          notifyCreditChange();
          toast.success("Job deadline extended and published successfully");
          const updated = {
            ...job,
            application_deadline: inlineDeadline,
            is_draft: false,
            is_closed: false,
            isClosed: false,
            is_paid: true
          };
          onUpdate(updated);
          setDetails(mapJob(updated));
          await onRefresh?.(Number(job.id));
          
          setStatusWarningModal({ show: false, title: '', message: '' });
          setInlineDeadline('');
        } else {
          toast.error(res?.message || 'Failed to publish job');
        }
      } else {
        toast.error('Failed to update deadline');
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || 'Error updating deadline');
    } finally {
      setIsUpdatingDeadline(false);
    }
  };

  const handleToggleStatusAction = (action: 'publish' | 'draft') => {
    if (!isDomainVerified) {
      setShowVerificationModal(true);
      return;
    }

    if (action === 'publish') {
      const deadlineStr = (job as any).application_deadline || (job as any).deadline;
      if (deadlineStr) {
        const deadlineDate = new Date(deadlineStr);
        if (!isNaN(deadlineDate.getTime())) {
          const now = new Date();
          const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
          const deadlineStart = new Date(deadlineDate.getFullYear(), deadlineDate.getMonth(), deadlineDate.getDate()).getTime();
          if (deadlineStart < todayStart) {
            const formattedDate = deadlineDate.toLocaleString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            });
            setStatusWarningModal({
              show: true,
              title: details.isClosed ? 'Cannot Publish Closed Job' : 'Cannot Publish Position',
              message: `The job position "${details.title}" cannot be published directly because its application deadline has already passed. Please edit the position to extend the required application deadline before publishing.`,
              deadlineInfo: `Job Application Deadline Passed On: ${formattedDate}`,
            });
            return;
          }
        }
      }
    }

    setJobToToggle(job);
  };

  const handleConfirmToggle = async () => {
    if (!jobToToggle) return;
    const targetJob = jobToToggle;
    setJobToToggle(null);
    const isActivating = Boolean((targetJob as any).is_draft || targetJob.is_closed || targetJob.isClosed);
    const action = isActivating ? 'publish' : 'draft';
    const isAlreadyPaid = Boolean(targetJob.is_paid ?? (targetJob as any).isPaid);

    if (isActivating && !isAlreadyPaid) {
      const confirmed = await requestConfirm({
        moduleKey: 'post_job',
        moduleName: 'Publish Job',
        featureKey: 'JOB_POST',
        creditCost: 5,
        actionDescription: 'Publishing this job position will deduct 5 credits from your balance.',
      });
      if (!confirmed) return;
    }

    try {
      setIsToggling(true);
      const res = action === 'publish'
        ? await apiService.bulkPublishJobs([Number(targetJob.id)])
        : await apiService.toggleJobStatus(Number(targetJob.id), { action });

      if (res?.status) {
        if (action === 'publish') {
          notifyCreditChange();
        }
        toast.success(action === 'publish' ? 'Job published successfully 🚀' : 'Job moved to Draft');
        const updated = {
          ...targetJob,
          is_draft: action === 'draft',
          is_closed: false,
          isClosed: false,
          is_paid: isActivating ? true : targetJob.is_paid,
        };
        onUpdate(updated);
        setDetails(mapJob(updated));
        await onRefresh?.(Number(targetJob.id));
      } else {
        toast.error(res?.message || 'Failed to update status');
      }
    } catch {
      toast.error('Error updating job status');
    } finally {
      setIsToggling(false);
    }
  };



  if (view === "edit") {
   const handleAddJob = async (data: NewJobData) => {
  try {
    const response = await apiService.addLookingFor(data);
    if (!response?.status) {
      toast.error(response?.message || "Update failed");
      return { status: false };
    }

    const jobId = resolveJobId(response, data.id ?? job.id);
    const selectedJobId = jobId != null && !Number.isNaN(jobId) ? jobId : (job.id != null ? Number(job.id) : null);

    toast.success("Job updated successfully 🚀");

    const updatedJob = {
      ...job,
      title: data.job_title,
      description: data.description,
    };

    onUpdate(updatedJob);
    setDetails(mapJob(updatedJob));
    // Refresh parent listing (prefer specific id) then navigate back to list
    if (selectedJobId != null) {
      await onRefresh?.(selectedJobId);
    } else {
      await onRefresh?.();
    }
    onBack();

    return { status: true };

  } catch (error: any) {
    toast.error(error?.message || "Update failed");
    return { status: false };
  }
};

    return (
      <AddJobModal
        jobDetails={job}
        onCancel={() => setView("details")}
        handleAddJob={handleAddJob}
      />
    );
  }

  const applicantFilters: ApplicantFilter[] = [
    "All",
    "Applied",
    "Shortlisted",
    "Scheduled",
    "Interviewed",
    "Hired",
    "Not Hired",
  ];


  const overviewRows: Array<{ label: string; value: React.ReactNode }> = [
    { label: "Job Position", value: details.title || "—" },
    // { label: "Department", value: details.department || "—" },
    { label: "Industry", value: details.industry || "—" },
    { label: "Location", value: details.location || "—" },
    { label: "Work Mode", value: details.workMode || "—" },
    { label: "Experience", value: details.experience || "—" },
    { label: "Salary", value: details.salary || "—" },
    { label: "Openings", value: details.openings || "—" },
{
  label: "Qualification",
  value: (
    <div className="uev-inline-tags">
      {(
        (() => {
          const q = details.qualification;

          if (Array.isArray(q)) return q;

          if (typeof q === "string") {
            try {
              return JSON.parse(q);
            } catch {
              return [];
            }
          }

          return [];
        })()
      ).length
        ? (() => {
            const parsed = Array.isArray(details.qualification)
              ? details.qualification
              : JSON.parse(details.qualification || "[]");

            return parsed.map((item: string) => (
              <span key={item} className="uev-mini-tag">
                {item}
              </span>
            ));
          })()
        : <span className="uev-mini-tag">Not specified</span>
      }
    </div>
  ),
},
    { label: "Gender Pref.", value: details.genderPreference || "—" },
    {
      label: "Emp. Type",
      value: (
        <div className="uev-inline-tags">
          {(details.employmentTypes.length ? details.employmentTypes : ["Not specified"]).map((item) => (
            <span key={item} className="uev-status-tag uev-status-tag--green">
              {item}
            </span>
          ))}
        </div>
      ),
    },
  ];

  return (
    <>
    {loading ? (
      <JobDetailViewSkeleton />
    ) : (
    <>
    <div className="ue-root">
      <div className="uev-shell">
        <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button
            type="button"
            className="uev-back-btn"
            onClick={onBack}
          >
            <ArrowLeft size={16} />
            Back to Job Listings
          </button>
        </div>

        <section className="uev-hero-card">
          <div className="uev-hero-main">
            <div className="uev-hero-icon">
              <Briefcase size={24} strokeWidth={1.9} />
            </div>
            <div className="uev-hero-copy">
              <h1 className="uev-title">{details.title}</h1>
              <div className="uev-meta-row">
                <span><FileText size={14} /> Posted: {details.posted || "—"}</span>
                <span><CalendarDays size={14} /> Deadline: {details.applicationDeadline}</span>
                <span><MapPin size={14} /> {details.location}</span>
                <span><Globe size={14} /> {details.workMode}</span>
              </div>
              <div className="uev-status-line">
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '4px 12px',
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 600,
                    background: details.isDraft ? '#f3e8ff' : details.isClosed ? '#fee2e2' : '#dcfce7',
                    color: details.isDraft ? '#7e22ce' : details.isClosed ? '#dc2626' : '#15803d',
                    border: `1px solid ${details.isDraft ? '#e9d5ff' : details.isClosed ? '#fca5a5' : '#bbf7d0'}`,
                  }}
                >
                  {!details.isDraft && !details.isClosed && <CircleCheckBig size={14} />}
                  {details.isDraft ? "Draft" : details.isClosed ? "Closed" : "Active"}
                </span>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "3px 10px",
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 600,
                    background: details.isPaid ? "#EFF6FF" : "#F9FAFB",
                    color: details.isPaid ? "#1D4ED8" : "#6B7280",
                    border: `1px solid ${details.isPaid ? "#BFDBFE" : "#E5E7EB"}`,
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: details.isPaid ? "#2563EB" : "#9CA3AF",
                      display: "inline-block",
                    }}
                  />
                  {details.isPaid ? "Paid" : "Unpaid"}
                </span>
              </div>
            </div>
          </div>
          <div className="uev-hero-actions" style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <button
              className="jl-btn jl-btn--edit"
              onClick={() => { if (onRequestEdit) onRequestEdit(job); else setView("edit"); }}
              style={{ minWidth: 'auto', padding: '0 16px', height: 36 }}
            >
              <Pencil size={14} className="me-1" />
              Edit
            </button>

            {(details.isDraft || details.isClosed) && (
              <button
                className="jl-btn jl-btn--toggle"
                onClick={() => handleToggleStatusAction('publish')}
                style={{ minWidth: 'auto', padding: '0 16px', height: 36 }}
              >
                <Play size={14} className="me-1" />
                Publish
              </button>
            )}

            {!details.isDraft && !details.isClosed && (
              <button
                className="jl-btn jl-btn--toggle"
                onClick={() => handleToggleStatusAction('draft')}
                style={{ minWidth: 'auto', padding: '0 16px', height: 36 }}
              >
                <RefreshCw size={14} className="me-1" />
                Move to Draft
              </button>
            )}

            {!details.isClosed && (
              <button
                className="jl-btn jl-btn--delete"
                onClick={() => {
                  setShowClosePosition(true);
                }}
                style={{ minWidth: 'auto', padding: '0 16px', height: 36 }}
              >
                <CircleOff size={14} className="me-1" />
                Close Position
              </button>
            )}
          </div>
        </section>

        <div className="uev-tabs">
          <button type="button" className={`uev-tab${activeTab === "overview" ? " uev-tab--active" : ""}`} onClick={() => setActiveTab("overview")}>
            <FileText size={15} />
            Overview
          </button>
          <button type="button" className={`uev-tab${activeTab === "candidates" ? " uev-tab--active" : ""}`} onClick={() => setActiveTab("candidates")}>
            <Users size={15} />
            Candidates
            <strong>{applicants.length || (job as any).applicants_count || 0}</strong>
          </button>
        </div>

        {/* ══ Info Cards Strip — Overview Key Details ═════════════════ */}
        {activeTab === "overview" && (
        <div className="uev-info-strip">
          <div className="uev-info-card">
            <div className="uev-info-icon uev-info-icon--blue">
              <Users size={16} />
            </div>
            <div>
              <div className="uev-info-label">OPEN POSITIONS</div>
              <div className="uev-info-val">{details.openings || "Still Hiring"}</div>
            </div>
          </div>
          <div className="uev-info-card">
            <div className="uev-info-icon uev-info-icon--yellow">
              <Banknote size={16} />
            </div>
            <div>
              <div className="uev-info-label">PACKAGE</div>
              <div className="uev-info-val">{details.salary || "—"}</div>
            </div>
          </div>
          <div className="uev-info-card">
            <div className="uev-info-icon uev-info-icon--purple">
              <Clock size={16} />
            </div>
            <div>
              <div className="uev-info-label">EXPERIENCE</div>
              <div className="uev-info-val">{details.experience || "—"}</div>
            </div>
          </div>
          <div className="uev-info-card">
            <div className="uev-info-icon uev-info-icon--green">
              <Briefcase size={16} />
            </div>
            <div>
              <div className="uev-info-label">EMP. TYPE</div>
              <div className="uev-info-val">
                {details.employmentTypes.length ? details.employmentTypes.join(', ') : "Not specified"}
              </div>
            </div>
          </div>
          <div className="uev-info-card">
            <div className="uev-info-icon uev-info-icon--blue">
              <GraduationCap size={16} />
            </div>
            <div>
              <div className="uev-info-label">QUALIFICATION</div>
              <div className="uev-info-val">
                {(() => {
                  const raw = details.qualification;
                  let parsed: string[] = [];
                  if (Array.isArray(raw)) {
                    parsed = raw;
                  } else if (typeof raw === "string") {
                    try {
                      const res = JSON.parse(raw);
                      parsed = Array.isArray(res) ? res : [raw];
                    } catch {
                      parsed = [raw];
                    }
                  }
                  return parsed.length ? parsed.join(', ') : "Not specified";
                })()}
              </div>
            </div>
          </div>
          <div className="uev-info-card">
            <div className="uev-info-icon uev-info-icon--yellow">
              <Users size={16} />
            </div>
            <div>
              <div className="uev-info-label">GENDER PREF.</div>
              <div className="uev-info-val">{details.genderPreference || "Any"}</div>
            </div>
          </div>
          <div className="uev-info-card">
            <div className="uev-info-icon uev-info-icon--green">
              <Globe size={16} />
            </div>
            <div>
              <div className="uev-info-label">INDUSTRY</div>
              <div className="uev-info-val">{details.industry || "—"}</div>
            </div>
          </div>
          <div className="uev-info-card">
            <div className="uev-info-icon uev-info-icon--red">
              <CalendarDays size={16} />
            </div>
            <div>
              <div className="uev-info-label">DEADLINE</div>
              <div className="uev-info-val">{details.applicationDeadline}</div>
            </div>
          </div>
        </div>
        )}

        {/* ══ Candidates Tab Content ════════════════════════════════ */}
        {activeTab === "candidates" && (
          <div className="uev-candidates-panel">
            <div className="uev-candidates-header">
              <div>
                <h2 className="uev-candidates-title">Applicants</h2>
                <p className="uev-candidates-sub">{applicants.length} candidate{applicants.length !== 1 ? "s" : ""} have applied for this position</p>
              </div>
            </div>

            <div className="qa-search-wrap">
              <Search size={16} className="qa-search-icon" />
              <input className="qa-search-input" placeholder="Search by name or email…" value={applicantSearch} onChange={e => setApplicantSearch(e.target.value)} />
            </div>

            <div className="qa-filter-tabs">
              {applicantFilters.map(f => {
                const count = f === "All" ? applicants.length : applicants.filter(a => a.status === f).length;
                return (
                  <button key={f} className={`qa-filter-tab${applicantFilter === f ? " qa-filter-tab--active" : ""}`} onClick={() => setApplicantFilter(f)}>
                    {f} <span className="qa-filter-count">{count}</span>
                  </button>
                );
              })}
            </div>

            <div className="uev-candidates-list">
              {isLoadingApplicants && Array.from({ length: 5 }).map((_, i) => (
                <ApplicantCardSkeleton key={i} />
              ))}
          
              {!isLoadingApplicants && filteredApplicants.map((a, i) => (
                <ApplicantCard
                  key={a.id || i}
                  applicant={a}
                  jobPositionId={Number(job.id)}
                  jobTitle={details.title}
                  statusStyle={statusStyle}
                  onStatusChange={handleApplicantStatusChange}
                  onUnblock={handleApplicantUnblocked}
                  connected={a.connected}
                  isDomainVerified={isDomainVerified}
                  onVerificationRequired={() => setShowVerificationModal(true)}
                />
              ))}
              {!isLoadingApplicants && filteredApplicants.length === 0 && (
                <p className="qa-empty">No applicants match your search.</p>
              )}
            </div>
          </div>
        )}

        {/* ══ Overview Tab Content ══════════════════════════════════ */}
        {activeTab === "overview" && (
          <div className="uev-grid">
          <div className="uev-main-column">
            <section className="uev-card">
              <div className="uev-card-head">
                <span className="uev-card-icon uev-card-icon--blue"><FileText size={16} /></span>
                <h3>Job Description</h3>
              </div>
              <div className="uev-description">
                {details.description ? (
                  <div dangerouslySetInnerHTML={{ __html: details.description }} />
                ) : (
                  <p>No description has been added for this job yet.</p>
                )}
              </div>
            </section>

            <div className="uev-dual-grid">
              <section className="uev-card">
                <div className="uev-card-head">
                  <span className="uev-card-icon uev-card-icon--green"><CircleCheckBig size={16} /></span>
                  <h3>Responsibilities</h3>
                </div>
                <ul className="uev-list uev-list--green">
                  {details.responsibilities.length ? details.responsibilities.map((item) => (
                    <li key={item}>{item}</li>
                  )) : <li>No responsibilities added yet.</li>}
                </ul>
              </section>

              <section className="uev-card">
                <div className="uev-card-head">
                  <span className="uev-card-icon uev-card-icon--purple"><GraduationCap size={16} /></span>
                  <h3>Requirements</h3>
                </div>
                <ul className="uev-list uev-list--blue">
                  {details.requirements.length ? details.requirements.map((item) => (
                    <li key={item}>{item}</li>
                  )) : <li>No requirements added yet.</li>}
                </ul>
              </section>
            </div>

            <section className="uev-card">
              <div className="uev-card-head">
                <span className="uev-card-icon uev-card-icon--amber"><Sparkles size={16} /></span>
                <h3>Required Skills</h3>
                <span className="uev-count">{details.skills.length}</span>
              </div>
              <div className="uev-pill-wrap">
                {details.skills.length ? details.skills.map((skill) => (
                  <span key={`${skill.id}-${skill.name}`} className="uev-skill-pill">
                    {skill.name}
                  </span>
                )) : <span className="uev-mini-tag">No skills added yet</span>}
              </div>
            </section>

            {/*
            <section className="uev-card">
              <div className="uev-card-head">
                <FileText size={20} className="uev-head-icon uev-head-icon--green" />
                <h3>Screening Questions</h3>
                <span className="uev-count">{details.screeningQuestions.length}</span>
              </div>
              <div className="uev-question-list">
                {details.screeningQuestions.length ? details.screeningQuestions.map((question, index) => (
                  <div key={`${question.text}-${index}`} className="uev-question-card">
                    <div className="uev-question-top">
                      <span className="uev-question-number">{index + 1}</span>
                      <div className="uev-question-copy">
                        <h4>{question.text}</h4>
                        <div className="uev-question-badges">
                          <span className="uev-mini-tag">{question.type === "single_choice" ? "Single Choice" : "Text"}</span>
                          {question.mandatory && <span className="uev-mini-tag uev-mini-tag--required">Required</span>}
                        </div>
                      </div>
                    </div>
                    {question.options?.length ? (
                      <div className="uev-pill-wrap uev-pill-wrap--tight">
                        {question.options.map((option) => (
                          <span key={option} className="uev-option-pill">{option}</span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                )) : <p className="qa-empty">No screening questions configured for this job.</p>}
              </div>
            </section>
            */}

            <section className="uev-card">
              <div className="uev-card-head">
                <span className="uev-card-icon uev-card-icon--gold"><Star size={16} /></span>
                <h3>Benefits & Perks</h3>
              </div>
              <div className="uev-benefit-grid">
                {details.benefits.length ? details.benefits.map((item) => (
                  <div key={item} className="uev-benefit-item">
                    <span className="uev-benefit-dot" />
                    {item}
                  </div>
                )) : <p className="qa-empty">No benefits added for this role.</p>}
              </div>
            </section>
          </div>

          <aside className="uev-side-column">
            <section className="uev-card">
              <div className="uev-card-head">
                <span className="uev-card-icon uev-card-icon--blue"><Zap size={16} /></span>
                <h3>Quick Actions</h3>
              </div>
              <div className="uev-action-list">
                <button type="button" className="uev-action-card" onClick={() => setActiveTab("candidates")}>
                  <span className="uev-action-icon uev-action-icon--blue"><Eye size={18} /></span>
                  <span>
                    <strong>View Applicants</strong>
                    <small>See live applicants for this role</small>
                  </span>
                </button>
                <button type="button" className="uev-action-card" onClick={openDuplicate}>
                  <span className="uev-action-icon uev-action-icon--green"><Copy size={18} /></span>
                  <span>
                    <strong>Duplicate Job</strong>
                    <small>Create a copy of this listing</small>
                  </span>
                </button>
              </div>
            </section>
          </aside>
        </div>
        )}
      </div>
    </div>

      {/* ══ Duplicate Modal ═══════════════════════════════════════ */}
      {showDuplicate && (
        <div className="qa-overlay" onClick={() => setShowDuplicate(false)}>
          <div className="qa-modal qa-modal--sm" onClick={e => e.stopPropagation()}>
            <button className="qa-close-btn qa-close-btn--abs" onClick={() => setShowDuplicate(false)}><X size={18} /></button>
            <div className="qa-modal-icon qa-modal-icon--green"><Copy size={24} /></div>
            <h2 className="qa-modal-title qa-modal-title--center">Duplicate Job Listing</h2>
            <p className="qa-modal-sub qa-modal-sub--center">This will create an exact copy of <strong>"{details.title}"</strong> with all details.</p>

            <div className="qa-field" ref={duplicateTitleRef} style={{ position: "relative" }}>
              <label className="qa-label">New Job Title</label>

              {/* Trigger — shows selected title, click to open search dropdown */}
              <div
                onClick={() => {
                  setIsDuplicateDesignationOpen(o => !o);
                  if (!isDuplicateDesignationOpen) fetchDuplicateDesignations(duplicateTitle);
                }}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "10px 14px", border: "1.5px solid #E2E8F0", borderRadius: 10,
                  background: "#fff", cursor: "pointer", userSelect: "none",
                  borderColor: isDuplicateDesignationOpen ? "#457EFF" : "#E2E8F0",
                  boxShadow: isDuplicateDesignationOpen ? "0 0 0 3px rgba(69,126,255,0.12)" : "none",
                  transition: "border-color 0.15s, box-shadow 0.15s",
                }}
              >
                <span style={{ fontSize: 13, color: duplicateTitle ? "#1E293B" : "#94A3B8", fontWeight: duplicateTitle ? 600 : 400 }}>
                  {duplicateTitle || "Select job title…"}
                </span>
                <svg width="16" height="16" viewBox="0 0 24 24" style={{ fill: "none", flexShrink: 0, transition: "transform 0.2s", transform: isDuplicateDesignationOpen ? "rotate(180deg)" : "rotate(0deg)" }} stroke="#94A3B8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>

              {/* Searchable dropdown */}
              {isDuplicateDesignationOpen && (
                <div style={{
                  position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 9999,
                  background: "#fff", border: "1.5px solid #E2E8F0", borderRadius: 10,
                  boxShadow: "0 8px 24px rgba(15,23,42,0.12)", overflow: "hidden",
                }}>
                  {/* Search input inside dropdown */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderBottom: "1px solid #F1F5F9" }}>
                    <Search size={14} style={{ color: "#94A3B8", flexShrink: 0 }} />
                    <input
                      autoFocus
                      placeholder="Search job title…"
                      style={{ border: "none", outline: "none", fontSize: 13, flex: 1, color: "#1E293B", background: "transparent" }}
                      onChange={e => fetchDuplicateDesignations(e.target.value)}
                    />
                  </div>

                  <div style={{ maxHeight: 200, overflowY: "auto" }}>
                    {isLoadingDuplicateDesignations ? (
                      <div style={{ padding: "12px 16px", color: "#94A3B8", fontSize: 13 }}>Searching…</div>
                    ) : duplicateDesignationList.length > 0 ? (
                      duplicateDesignationList.map(item => (
                        <button
                          key={item.id}
                          type="button"
                          style={{
                            width: "100%", textAlign: "left", padding: "10px 16px",
                            border: "none", background: item.id === duplicateDesignationId ? "#EEF4FF" : "none",
                            cursor: "pointer", fontSize: 13,
                            color: item.id === duplicateDesignationId ? "#457EFF" : "#1E293B",
                            fontWeight: item.id === duplicateDesignationId ? 700 : 400,
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            borderBottom: "1px solid #F8FAFC",
                          }}
                          onMouseEnter={e => { if (item.id !== duplicateDesignationId) e.currentTarget.style.background = "#F8FAFC"; }}
                          onMouseLeave={e => { if (item.id !== duplicateDesignationId) e.currentTarget.style.background = "none"; }}
                          onClick={() => {
                            setDuplicateTitle(item.name);
                            setDuplicateDesignationId(item.id);
                            setIsDuplicateDesignationOpen(false);
                            setDuplicateDesignationList([]);
                          }}
                        >
                          {item.name}
                          {item.id === duplicateDesignationId && (
                            <svg width="14" height="14" viewBox="0 0 24 24" style={{ fill: "none" }} stroke="#457EFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </button>
                      ))
                    ) : (
                      <div style={{ padding: "12px 16px", color: "#94A3B8", fontSize: 13 }}>
                        {isLoadingDuplicateDesignations ? "Searching…" : "Type to search job titles"}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="qa-info-box">
              <p className="qa-info-box-title">WHAT WILL BE COPIED</p>
              {["Job description & requirements", "Skills & qualifications", "Salary & benefits", "Employment type & location"].map(item => (
                <div key={item} className="qa-info-box-row">
                  <CircleCheckBig size={16} color="#27a665" />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <div className="qa-modal-actions">
              <button className="qa-btn qa-btn--ghost" onClick={() => setShowDuplicate(false)}>Cancel</button>
              <button className="qa-btn qa-btn--primary" onClick={handleDuplicate} disabled={isDuplicating || !duplicateTitle.trim()}>
                <Copy size={16} /> {isDuplicating ? "Duplicating…" : "Duplicate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Close Position Modal ══════════════════════════════════ */}
      {showClosePosition && (
        <div className="qa-overlay" onClick={() => setShowClosePosition(false)}>
          <div className="qa-modal qa-modal--sm" onClick={e => e.stopPropagation()}>
            <button className="qa-close-btn qa-close-btn--abs" onClick={() => setShowClosePosition(false)}><X size={18} /></button>
            <div className="qa-modal-icon qa-modal-icon--red"><AlertTriangle size={24} /></div>
            <h2 className="qa-modal-title qa-modal-title--center">Close Position</h2>
            <p className="qa-modal-sub qa-modal-sub--center">Are you sure you want to close <strong>"{details.title}"</strong>? This will stop accepting new applications.</p>

            <div className="qa-warn-box">
              <div className="qa-warn-box-title"><AlertTriangle size={14} /> ACTIVE CANDIDATES WILL BE AFFECTED</div>
              <div className="qa-warn-box-row"><Clock size={14} color="#d68600" /><span>Applicants will no longer be able to apply once this position is closed.</span></div>
              <div className="qa-warn-box-row"><Users size={14} color="#2c63d6" /><span>Existing candidate records and interview progress will remain available.</span></div>
            </div>

            <div className="qa-modal-actions">
              <button className="qa-btn qa-btn--ghost" onClick={() => setShowClosePosition(false)}>Keep Open</button>
              <button className="qa-btn qa-btn--danger" onClick={handleClosePosition} disabled={isClosing}>
                <XCircle size={16} /> {isClosing ? "Closing…" : "Close Position"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
    )}
    {/* Verification Required Modal */}
          <VerificationRequiredModal
            show={showVerificationModal}
            onHide={() => setShowVerificationModal(false)}
          />

    {/* Status Warning Modal */}
          <Modal
            show={statusWarningModal.show}
            onHide={() => setStatusWarningModal({ show: false, title: '', message: '' })}
            centered
            contentClassName="border-0 shadow"
            style={{ '--bs-modal-bg': '#ffffff', '--bs-modal-border-radius': '12px' } as React.CSSProperties}
          >
            <Modal.Header closeButton className="border-0 pb-0" />
            <Modal.Body className="text-center px-4 pb-4 pt-0">
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: '#fef2f2',
                  border: '1px solid #fee2e2',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                }}
              >
                <XCircle size={32} color="#dc2626" />
              </div>
              <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: 8, fontSize: 20 }}>
                {statusWarningModal.title}
              </h4>
              <p style={{ color: '#475569', fontSize: 14, marginBottom: statusWarningModal.deadlineInfo ? 20 : 24 }}>
                {statusWarningModal.message}
              </p>

              {statusWarningModal.deadlineInfo && (
                <div className="mb-4">
                  <div style={{ color: '#1e293b', fontWeight: 600, fontSize: 14, marginBottom: 12, textAlign: 'left' }}>
                    Job requiring new deadline:
                  </div>
                  <div className="d-flex flex-column gap-3 text-start">
                    <div className="d-flex align-items-center justify-content-between p-3 rounded" style={{ background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                      <div style={{ overflow: 'hidden', paddingRight: '12px' }}>
                        <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {details.title}
                        </div>
                        <div style={{ fontSize: 12, color: '#ef4444', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444', flexShrink: 0 }}></div>
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            Expired: {statusWarningModal.deadlineInfo.replace('Job Application Deadline Passed On: ', '')}
                          </span>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2" style={{ flexShrink: 0 }}>
                        <input
                          type="date"
                          className="form-control form-control-sm shadow-none"
                          min={new Date().toISOString().split('T')[0]}
                          value={inlineDeadline}
                          onChange={(e) => setInlineDeadline(e.target.value)}
                          style={{ width: 130, fontSize: 13, borderColor: '#cbd5e1', borderRadius: '6px' }}
                        />
                        <Button
                          variant="light"
                          size="sm"
                          className="shadow-sm"
                          disabled={isUpdatingDeadline || !inlineDeadline}
                          onClick={handleUpdateDeadlineAndPublish}
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            padding: '4px 12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#ffffff',
                            border: '1px solid #cbd5e1',
                            color: '#002781',
                            borderRadius: '6px',
                            transition: 'all 0.2s ease-in-out',
                            whiteSpace: 'nowrap',
                            width: 'auto',
                            minWidth: '70px'
                          }}
                        >
                          {isUpdatingDeadline ? 'Publishing...' : 'Publish'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
                <Button
                  variant="primary"
                  onClick={() => setStatusWarningModal({ show: false, title: '', message: '' })}
                  style={{ minWidth: 140, borderRadius: '8px', background: '#002781', borderColor: '#002781', fontWeight: 600, padding: '8px 24px' }}
                >
                  Got It
                </Button>
              </div>
            </Modal.Body>
          </Modal>
    {/* Toggle Status Confirmation Modal */}
    <Modal
      show={!!jobToToggle}
      onHide={() => !isToggling && setJobToToggle(null)}
      centered
    >
      <Modal.Header closeButton={!isToggling} className="border-0 pb-0" />

      <Modal.Body className="text-center px-4 pb-4 pt-1">
        <div style={{ margin: '0 auto 16px', background: '#F8FAFC', border: '1px solid #E2E8F0', width: 90, height: 90, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {Boolean((jobToToggle as any)?.is_draft || jobToToggle?.is_closed || jobToToggle?.isClosed) ? (
            <Send size={40} color="#15803d" />
          ) : (
            <FileText size={40} color="#b45309" />
          )}
        </div>

        <h4 style={{ fontWeight: 700, color: '#1f2937', marginBottom: 10 }}>
          {Boolean((jobToToggle as any)?.is_draft || jobToToggle?.is_closed || jobToToggle?.isClosed)
            ? 'Publish Job'
            : 'Move Job to Draft'}
        </h4>

        <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 24 }}>
          Are you sure you want to{' '}
          <strong>
            {Boolean((jobToToggle as any)?.is_draft || jobToToggle?.is_closed || jobToToggle?.isClosed) ? 'publish' : 'move to draft'}
          </strong>{' '}
          <strong>{jobToToggle?.title || 'this job'}</strong>?
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
          <style>{`
            button.btn.jl-modal-action-btn {
              border-radius: 10px !important;
              padding: 8px 12px !important;
              white-space: nowrap !important;
            }
            button.btn.btn-primary.jl-modal-action-btn {
              background-color: #002781 !important;
              border-color: #002781 !important;
              color: #ffffff !important;
              border-radius: 10px !important;
            }
          `}</style>
          <Button
            variant="outline-secondary"
            className="jl-modal-action-btn"
            onClick={() => setJobToToggle(null)}
            disabled={isToggling}
            style={{ width: 160 }}
          >
            Cancel
          </Button>

          <Button
            variant="primary"
            className="jl-modal-action-btn"
            onClick={handleConfirmToggle}
            disabled={isToggling}
            style={{ width: 160 }}
          >
            {isToggling ? (
              'Updating...'
            ) : Boolean((jobToToggle as any)?.is_draft || jobToToggle?.is_closed || jobToToggle?.isClosed) ? (
              'Publish'
            ) : (
              'Move to Draft'
            )}
          </Button>
        </div>
      </Modal.Body>
    </Modal>

    {/* Credit Confirmation Modal */}
    {isCreditPopupOpen && creditPopupOptions && (
      <CreditConfirmPopup
        isOpen={isCreditPopupOpen}
        onCancel={handleCreditCancel}
        onConfirm={handleCreditConfirm}
        onNeverShowAgain={handleCreditNeverShowAgain}
        featureKey={creditPopupOptions.featureKey}
        moduleKey={creditPopupOptions.moduleKey}
        moduleName={creditPopupOptions.moduleName}
        creditCost={creditPopupOptions.creditCost}
        actionDescription={creditPopupOptions.actionDescription}
      />
    )}
    </>
  );
};

export default JobDetailView;
